import React, { useState } from 'react';
import './ShareJobModal.css';

const ShareJobModal = ({ job, onClose }) => {
    const [copySuccess, setCopySuccess] = useState('');
    const [isSharing, setIsSharing] = useState(false);

    // Detect if Web Share API is available (mobile)
    const canUseNativeShare = navigator.share !== undefined;

    // Generate shareable message
    const getShareMessage = () => {
        return `Hey, take a look at this job from ${job.organization}. Let me know what you think.\n\n${job.link}`;
    };

    // Generate email subject
    const getEmailSubject = () => {
        return `RFQ Opportunity - ${job.organization}`;
    };

    // Handle native share (mobile)
    const handleNativeShare = async () => {
        if (!canUseNativeShare) return;

        setIsSharing(true);
        try {
            await navigator.share({
                title: `RFQ from ${job.organization}`,
                text: `Hey, take a look at this job from ${job.organization}. Let me know what you think.`,
                url: job.link
            });
            setCopySuccess('Shared successfully!');
            setTimeout(() => {
                setCopySuccess('');
                onClose();
            }, 1500);
        } catch (err) {
            // User cancelled or share failed
            if (err.name !== 'AbortError') {
                console.error('Share failed:', err);
                setCopySuccess('Share failed. Please try again.');
            }
        } finally {
            setIsSharing(false);
        }
    };

    // Handle email share (desktop)
    const handleEmailShare = () => {
        const subject = encodeURIComponent(getEmailSubject());
        const body = encodeURIComponent(getShareMessage());
        const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
        
        window.location.href = mailtoLink;
        
        setCopySuccess('Opening email client...');
        setTimeout(() => {
            setCopySuccess('');
            onClose();
        }, 2000);
    };

    // Fallback copy function for non-secure contexts (HTTP on mobile)
    const fallbackCopy = (text) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
            document.execCommand('copy');
            textArea.remove();
            return true;
        } catch (err) {
            textArea.remove();
            return false;
        }
    };

    // Handle copy link
    const handleCopyLink = async () => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(job.link);
            } else {
                // Fallback for non-secure contexts (HTTP)
                if (!fallbackCopy(job.link)) {
                    throw new Error('Fallback copy failed');
                }
            }
            setCopySuccess('Link copied to clipboard!');
            setTimeout(() => setCopySuccess(''), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            setCopySuccess('Failed to copy. Please try manually.');
        }
    };

    // Handle copy full message
    const handleCopyMessage = async () => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(getShareMessage());
            } else {
                // Fallback for non-secure contexts (HTTP)
                if (!fallbackCopy(getShareMessage())) {
                    throw new Error('Fallback copy failed');
                }
            }
            setCopySuccess('Message copied to clipboard!');
            setTimeout(() => setCopySuccess(''), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            setCopySuccess('Failed to copy. Please try manually.');
        }
    };

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target.classList.contains('share-modal-backdrop')) {
            onClose();
        }
    };

    return (
        <div className="share-modal-backdrop" onClick={handleBackdropClick}>
            <div className="share-modal">
                <div className="share-modal-header">
                    <h3>Share Job</h3>
                    <button className="share-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="share-modal-body">
                    <div className="share-job-info">
                        <p className="share-job-title">{job.title || 'RFQ Opportunity'}</p>
                        <p className="share-job-org">{job.organization}</p>
                    </div>

                    {copySuccess && (
                        <div className="share-success-message">
                            {copySuccess}
                        </div>
                    )}

                    {canUseNativeShare ? (
                        // Mobile: Show native share button
                        <div className="share-options">
                            <button 
                                className="share-option-btn share-btn-primary"
                                onClick={handleNativeShare}
                                disabled={isSharing}
                            >
                                <span className="share-icon">📤</span>
                                Share
                            </button>
                        </div>
                    ) : (
                        // Desktop: Show multiple options
                        <div className="share-options">
                            <button 
                                className="share-option-btn share-btn-primary"
                                onClick={handleEmailShare}
                            >
                                <span className="share-icon">✉️</span>
                                Email
                            </button>

                            <button 
                                className="share-option-btn"
                                onClick={handleCopyMessage}
                            >
                                <span className="share-icon">📋</span>
                                Copy Message
                            </button>

                            <button 
                                className="share-option-btn"
                                onClick={handleCopyLink}
                            >
                                <span className="share-icon">🔗</span>
                                Copy Link
                            </button>
                        </div>
                    )}

                    <div className="share-preview">
                        <p className="share-preview-label">Preview:</p>
                        <div className="share-preview-content">
                            {getShareMessage()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareJobModal;

